import { type RefObject, useContext, useEffect, useMemo, useRef, useState } from "react";

import { noop } from "lodash";

import useIsElementVisible from "~/lib/hooks/useElementIsVisible";

import { DismissibleElementsContext } from "../common/DismissibleElements";
import { useTourContext } from "./TourController";
import type { OnSightTourKey } from "./Tours";

/**
 * Returns a ref that can be used to trigger a tour when the referenced element is in view.
 *
 * @param key - The key of the tour to start.
 * @returns A ref for an element whose visibility will trigger the tour.
 */
export default function useStartTourOnSight(key: OnSightTourKey): RefObject<HTMLElement> {
	const { startTour, activeTour, activeTourKey, abortTour } = useTourContext();
	const { hasAcknowledgementsRemaining } = useContext(DismissibleElementsContext);

	const visibilityRef = useRef<HTMLElement>(null);
	const triggerIsVisible = useIsElementVisible(visibilityRef);

	// This is a temporary solution so the our doesn't overlap with other potential
	// modals that might be triggered at the same time. The proper fix for this is more complicated
	// and will be done in a follow-up ticket.
	const [visibleForThreeSeconds, setVisibleForThreeSeconds] = useState(false);
	useEffect(() => {
		function confirmVisible() {
			setVisibleForThreeSeconds(true);
		}
		if (triggerIsVisible) {
			const timeout = setTimeout(confirmVisible, 3000);
			return () => clearTimeout(timeout);
		} else {
			setVisibleForThreeSeconds(false);
			return noop;
		}
	}, [triggerIsVisible]);

	const shouldTrigger = useMemo(() => {
		return hasAcknowledgementsRemaining(key);
	}, [hasAcknowledgementsRemaining, key]);

	useEffect(() => {
		const isAlreadyActive = activeTourKey === key;
		if (shouldTrigger && visibleForThreeSeconds && !activeTour) {
			startTour(key);
		} else if (!shouldTrigger && isAlreadyActive) {
			abortTour();
		}
	}, [shouldTrigger, startTour, key, activeTour, abortTour, activeTourKey, visibleForThreeSeconds]);

	return visibilityRef;
}
